import React, { ButtonHTMLAttributes, ReactNode } from 'react';
import classNames from 'classnames';

export const PROFESSION_COLORS = [
  '#E8913A',
  '#1C7A5A',
  '#2D9CDB',
  '#9B59B6',
  '#D4546A',
  '#2C3E50',
];

// #2C3E50 is a dark navy: on the light rows it reads fine, but on the dark
// surface (#18212F) it measures ~1.4:1 and the dot effectively disappears.
// Dark swaps only that one entry for a lighter slate of the same family.
const PROFESSION_COLORS_DARK = PROFESSION_COLORS.map((c) =>
  c === '#2C3E50' ? '#6C87A6' : c
);

export const getProfessionColor = (id: number, dark = false) =>
  (dark ? PROFESSION_COLORS_DARK : PROFESSION_COLORS)[
    Math.abs(id) % PROFESSION_COLORS.length
  ];

type Tone = 'default' | 'confirm' | 'danger';

interface ActionIconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: Tone;
}

const TONE_CLASSES: Record<Tone, string> = {
  default: 'text-muted hover:bg-surface-2 hover:text-text-2',
  confirm: 'text-up hover:bg-up-bg',
  danger: 'text-muted hover:bg-down-bg hover:text-down',
};

export const ActionIconButton = ({
  tone = 'default',
  className,
  children,
  ...rest
}: ActionIconButtonProps) => (
  <button
    type="button"
    {...rest}
    className={classNames(
      'w-7 h-7 rounded-md flex items-center justify-center transition-colors shrink-0 bg-transparent border-0 cursor-pointer p-0',
      TONE_CLASSES[tone],
      className,
    )}
  >
    {children}
  </button>
);

interface FlashWrapperProps {
  flash?: '' | 'green' | 'red';
  children: ReactNode;
  className?: string;
}

export const FlashWrapper = React.forwardRef<HTMLDivElement, FlashWrapperProps>(
  ({ flash, children, className }, ref) => (
    <div
      ref={ref}
      className={classNames(
        'relative transition-colors',
        flash === 'green' && 'bg-up-bg',
        flash === 'red' && 'bg-down-bg',
        className,
      )}
    >
      {children}
    </div>
  ),
);

export const SubLine = ({ color }: { color: string }) => (
  <div
    className="w-3 h-4 border-l-[1.5px] border-b-[1.5px] rounded-bl ml-1 mb-1 shrink-0"
    style={{ borderColor: color }}
  />
);

interface InlineInputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

export const InlineInput = React.forwardRef<HTMLInputElement, InlineInputProps>(
  ({ className, ...rest }, ref) => (
    <input
      ref={ref}
      {...rest}
      className={classNames(
        'flex-1 min-w-0 px-2.5 py-1.5 text-base text-text-1 bg-surface border-[1.5px] border-accent rounded-md outline-none font-sans',
        className,
      )}
    />
  ),
);
